import {
  Box,
  Flex,
  Text,
  Center,
  useColorModeValue,
  useBreakpointValue,
} from '@chakra-ui/react';
import React from 'react';

import type { Transaction } from 'types/api/transaction';

import { route } from 'nextjs-routes';

import { getDisplayTxNativeValueWei } from 'lib/explorer/displayOverrides';
import getValueWithUnit from 'lib/getValueWithUnit';
import getTxErrorText from 'lib/tx/getTxErrorText';
import { currencyUnits } from 'lib/units';
import Skeleton from 'ui/shared/chakra/Skeleton';
import LinkInternal from 'ui/shared/links/LinkInternal';
import TxStatus from 'ui/shared/statusTag/TxStatus';
import TimeAgoWithTooltip from 'ui/shared/TimeAgoWithTooltip';

type Props = {
  tx: Transaction;
  isLoading?: boolean;
};

const LatestTxsItem = ({ tx, isLoading }: Props) => {
  const dataTo = tx.to ? tx.to : tx.created_contract;
  const errorText = getTxErrorText(tx);
  const borderColor = useColorModeValue('#e9ecef', 'gray.600');
  const iconBg = useColorModeValue('#f8f9fa', 'gray.700');
  const iconStroke = useColorModeValue('#77838f', 'gray.400');
  const mutedColor = useColorModeValue('#6c757d', 'gray.400');
  const labelColor = useColorModeValue('#000', 'gray.200');
  const pillBorder = useColorModeValue('#00000026', 'gray.500');
  const pillColor = useColorModeValue('#000', 'gray.200');
  const failedIconBg = useColorModeValue('#fdecea', 'red.900');
  const failedIconStroke = useColorModeValue('#dc3545', 'red.300');

  const isMobile = useBreakpointValue({ base: true, md: false });
  const isFailed = tx.status === 'error';

  // Desktop layout (original)
  const desktopLayout = (
    <Flex
      alignItems="center"
      py={4}
      px={5}
      borderBottom="1px solid"
      borderBottomColor={borderColor}
      _last={{ borderBottom: 'none' }}
    >
      {/* Document Icon */}
      <Center
        w="52px"
        h="52px"
        bg={isFailed ? failedIconBg : iconBg}
        borderRadius="10px"
        flexShrink={0}
        mr={4}
      >
        <Skeleton isLoaded={!isLoading}>
          <Box as="svg" viewBox="0 0 24 24" fill="none" stroke={isFailed ? failedIconStroke : iconStroke} strokeWidth="1" w="24px" h="24px">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <line x1="10" y1="9" x2="8" y2="9"/>
          </Box>
        </Skeleton>
      </Center>

      {/* TX Hash & Time */}
      <Box minW="140px" mr={4}>
        <Skeleton isLoaded={!isLoading}>
          <LinkInternal
            href={route({ pathname: '/tx/[hash]', query: { hash: tx.hash } })}
            fontSize="15px"
            fontWeight={500}
            color="#066a9c"
            _hover={{ color: '#066a9c' }}
          >
            {tx.hash.slice(0, 13)}...
          </LinkInternal>
        </Skeleton>
        <Flex alignItems="center" gap={2} mt={1} flexWrap="wrap">
          <TxStatus status={tx.status} errorText={errorText} isLoading={isLoading}/>
          <TimeAgoWithTooltip
            timestamp={tx.timestamp}
            enableIncrement={!isLoading}
            isLoading={isLoading}
            color={mutedColor}
            fontSize="13px"
          />
        </Flex>
      </Box>

      {/* From/To Addresses */}
      <Box flex={1} minW={0}>
        <Flex alignItems="center" gap={2} fontSize="14px" mb={1}>
          <Text color={labelColor} flexShrink={0}>From</Text>
          <Skeleton isLoaded={!isLoading}>
            <LinkInternal
              href={route({ pathname: '/address/[hash]', query: { hash: tx.from.hash } })}
              fontSize="14px"
              fontWeight={500}
              color="#066a9c"
              _hover={{ color: '#066a9c' }}
            >
              {tx.from.hash.slice(0, 10)}...{tx.from.hash.slice(-9)}
            </LinkInternal>
          </Skeleton>
        </Flex>
        {dataTo && (
          <Flex alignItems="center" gap={2} fontSize="14px">
            <Text color={labelColor} flexShrink={0}>To</Text>
            <Skeleton isLoaded={!isLoading}>
              <LinkInternal
                href={route({ pathname: '/address/[hash]', query: { hash: dataTo.hash } })}
                fontSize="14px"
                fontWeight={500}
                color="#066a9c"
                _hover={{ color: '#066a9c' }}
              >
                {dataTo.hash.slice(0, 10)}...{dataTo.hash.slice(-9)}
              </LinkInternal>
            </Skeleton>
          </Flex>
        )}
      </Box>

      {/* Value Pill */}
      <Skeleton isLoaded={!isLoading}>
        <Box
          border="1px solid"
          borderColor={pillBorder}
          borderRadius="6px"
          px={3}
          py={1.5}
          fontSize="10.9px"
          fontWeight={500}
          color={pillColor}
          whiteSpace="nowrap"
          ml={3}
        >
          {getValueWithUnit(getDisplayTxNativeValueWei(tx.hash, tx.value)).dp(5).toFormat()} {currencyUnits.ether}
        </Box>
      </Skeleton>
    </Flex>
  );

  // Mobile layout (matches screenshot)
  const mobileLayout = (
    <Flex
      py={4}
      px={5}
      borderBottom="1px solid"
      borderBottomColor={borderColor}
      _last={{ borderBottom: 'none' }}
      direction="column"
      gap={2}
    >
      {/* Row 1: TX# and time */}
      <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
        <Skeleton isLoaded={!isLoading} display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Text as="span" fontSize="15px" fontWeight={500} color={labelColor}>
            TX#
          </Text>
          <LinkInternal
            href={route({ pathname: '/tx/[hash]', query: { hash: tx.hash } })}
            fontSize="15px"
            fontWeight={500}
            color="#066a9c"
            _hover={{ color: '#066a9c' }}
          >
            {tx.hash.slice(0, 13)}...
          </LinkInternal>
          <TxStatus status={tx.status} errorText={errorText} isLoading={isLoading}/>
        </Skeleton>
        <TimeAgoWithTooltip
          timestamp={tx.timestamp}
          enableIncrement={!isLoading}
          isLoading={isLoading}
          color={mutedColor}
          fontSize="13px"
        />
      </Flex>

      {/* Row 2: From address */}
      <Flex align="center" gap={2} fontSize="14px">
        <Text color={labelColor} flexShrink={0}>From</Text>
        <Skeleton isLoaded={!isLoading}>
          <LinkInternal
            href={route({ pathname: '/address/[hash]', query: { hash: tx.from.hash } })}
            fontSize="14px"
            fontWeight={500}
            color="#066a9c"
            _hover={{ color: '#066a9c' }}
          >
            {tx.from.hash.slice(0, 10)}...{tx.from.hash.slice(-9)}
          </LinkInternal>
        </Skeleton>
      </Flex>

      {/* Row 3: To address + value pill */}
      {dataTo && (
        <Flex align="center" justify="space-between" gap={2}>
          <Flex align="center" gap={2} minW={0} flex={1}>
            <Text color={labelColor} flexShrink={0}>To</Text>
            <Skeleton isLoaded={!isLoading}>
              <LinkInternal
                href={route({ pathname: '/address/[hash]', query: { hash: dataTo.hash } })}
                fontSize="14px"
                fontWeight={500}
                color="#066a9c"
                _hover={{ color: '#066a9c' }}
              >
                {dataTo.hash.slice(0, 10)}...{dataTo.hash.slice(-9)}
              </LinkInternal>
            </Skeleton>
          </Flex>
          <Skeleton isLoaded={!isLoading}>
            <Box
              border="1px solid"
              borderColor={pillBorder}
              borderRadius="6px"
              px={3}
              py={1.5}
              fontSize="10.9px"
              fontWeight={500}
              color={pillColor}
              whiteSpace="nowrap"
            >
              {getValueWithUnit(getDisplayTxNativeValueWei(tx.hash, tx.value)).dp(5).toFormat()} {currencyUnits.ether}
            </Box>
          </Skeleton>
        </Flex>
      )}
    </Flex>
  );

  return isMobile ? mobileLayout : desktopLayout;
};

export default React.memo(LatestTxsItem);